// VoiceSpectrum.Web.T2 · v10.0.10.10 · 2026-05-19 · VoiceSpectrum AI
//
// Modal — v2.2 pattern (scrim rgba(15,23,42,.55), 440px container by default).
// Mounts on document.body via React portal; locks body scroll while open;
// closes on Escape / scrim click / × button.

import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  size?: 'md' | 'lg';
  /** Force-close on scrim click? Defaults to true. Set false for forms with unsaved data. */
  closeOnScrim?: boolean;
  /** Force-close on Escape? Defaults to true. */
  closeOnEscape?: boolean;
  children: ReactNode;
  /** Right-aligned actions slot — typically <Button> elements. */
  actions?: ReactNode;
}

export default function Modal({
  open, title, onClose,
  size = 'md',
  closeOnScrim = true, closeOnEscape = true,
  children, actions,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && closeOnEscape) {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, closeOnEscape]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        if (!closeOnScrim) return;
        // Only close when the scrim itself is the mousedown target — clicks inside
        // the modal container shouldn't bubble-close.
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      <div className={`modal ${size === 'lg' ? 'modal-lg' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button type="button" className="slider-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}
